import { Component, OnInit, AfterContentInit, AfterViewInit, Renderer, DoCheck, OnDestroy, Input, Output, EventEmitter,
  ElementRef, ViewChild, ViewChildren, ContentChild, ContentChildren, QueryList, IterableDiffers } from '@angular/core';
import { CfCoreComponent } from '../core/core.component';
import { CfDatatablePagerComponent } from '../datatable-pager/datatable-pager.component';
import { CfDatatableColumnComponent } from '../datatable-column/datatable-column.component';
import { CfDatatableFilterComponent } from '../datatable-filter/datatable-filter.component';
import { CfDatatableRowsExpanderComponent } from '../datatable-rows-expander/datatable-rows-expander.component';
import { IconModel } from '../../models/icon/icon.model';
import { DatatableModel } from '../../models/datatable/datatable.model';
import { DatatableStylingModel } from '../../models/datatable/datatable-styling.model';
import { DatatableTemplates } from '../../templates/datatable.template';
import { TemplateService } from '../../services/template-service/template.service';
/**
 * <p>CfDatatable component is built using
 * <a href="https://github.com/swimlane/ngx-datatable" target="_blank">ngx-datatable</a>, CfIcon and CfInput.</p>
 * <pre>
 * <code>
 * import {{'{'}} DatatableModel {{'}'}} from 'cedrus-fusion'
 * import {{'{'}} DatatableStylingModel {{'}'}} from 'cedrus-fusion'
 * <code><</code>cf-datatable<code>></code><code><</code><code>/</code>cf-datatable<code>></code>
 * </code>
 * </pre>
 */
export class CfDatatableComponent extends CfCoreComponent implements OnInit, AfterContentInit, AfterViewInit, DoCheck {
/**
 * @hidden 
 * List of datatable column components with column templates and names.
 */
tableColumns: QueryList<CfDatatableColumnComponent>;
/**
 * @hidden 
 * Datatable row details template
 */
details;
/**
 * @hidden
 */
cfDatatablePager: CfDatatablePagerComponent;
/**
 * @hidden
 */
cfDatatableFilter: CfDatatableFilterComponent;
/**
 * @hidden
 */
cfDatatableRowsExpander: CfDatatableRowsExpanderComponent;
/**
 * @hidden 
 * Component property for registering Datatable and for connection to <b>ngx-datatable</b> functionality
 */
datatableElement;
/**
 * @hidden
 */
cfDatatableViewColumns: QueryList<any>;
/**
 * @hidden 
 * Datatable original rows.
 * Needs to be to keep original rows available after iteracting with datatable functionality
 * (for exapmle after filtering).
 */
originalRows: any[] = [];
/**
 * @hidden 
 * Working rows list which is changing by user iteraction with datatable functionality.
 */
clonedRows: any[] = [];
/**
 * <code><pre>{{'{'}}
 * 	showTableFooter: boolean,
 * 	showTableCount: boolean,
 * 	showTablePager: boolean,
 * 	headerHeight: any,
 * 	footerHeight: any,
 * 	rows: any[],
 * 	rowHeight: any,
 * 	columnMode: string,
 * 	limit: number,
 * 	expandable: boolean,
 * 	rowExpand: string,
 * 	detailsHeight: number,
 * 	expandIcon: IconModel,
 * 	collapseIcon: IconModel,
 * 	selectable: boolean,
 * 	sorted: any[],
 * 	selected: any[],
 * {{'}'}}</pre></code>
 * <h4>showTableFooter: boolean</h4>
 * If to show ngx-datatable footer
 * <h4>showTableCount: boolean</h4>
 * If to show ngx-datatable footer count text
 * <h4>showTablePager: boolean</h4>
 * If to show ngx-datatable footer pager
 * <h4>headerHeight: any</h4>
 * The height of the header in pixels. Type any. Pass a false for no header. Default: 42.
 * <h4>footerHeight: any</h4>
 * The height of the footer in pixels. Type any. Pass a false for no footer. Default: 42.
 * <h4>rows: any[]</h4>
 * It is array with rows json objects.
 * <h4>rowHeight: any</h4>
 * It is height of each row. Can be numeric (nember will be transformed to pixels) or 'auto'. Default: 50.
 * <h4>columnMode: string</h4>
 * The mode which the columns are distributed across the table. Type string. Can be: flex, force, standard. Default: force.
 * <h4>limit: number</h4>
 * It is rows quontity per page. Type number. Default: 3. To show all rows: -1.
 * <h4>expandable: boolean</h4>
 * It is boolean value to use or not row details section. Default: false.
 * <h4>rowExpand: string</h4>
 * Means how rows can be expanded at one time. Can be: multiple or single. Default: multiple.
 * <h4>detailsHeight: number</h4>
 * It is height of row in pixels. Type number. Default: 130.
 * <h4>expandIcon: IconModel</h4>
 * CfIcon component for the row expanding icon.
 * <h4>collapseIcon: IconModel</h4>
 * CfIcon component for the row collapsing icon.
 * <h4>selectable: boolean</h4>
 * It is boolean value to show or not rows section checkboxes. Default: false.
 * <h4>sorted: any[]</h4>
 * Array with sorting objects. Default: [].
 * Rows can sorted by multipe columns. Each sorting object is a json object with two properties:
 * <b>prop</b>(property name by which to sort rows) and <b>dir</b>(sort direction. Can be: <b>asc</b> or <b>desc</b>).
 * <h4>selected: any[]</h4>
 * Array for selected items.
 * <h4>sorted: array</h4>
 * Array with sorting objects. Default: [].
 * Rows can sorted by multipe columns.
 * Each sorting object is a json object with two properties:
 * <b>prop</b>(property name by which to sort rows) and <b>dir</b>(sort direction. Can be: <b>asc</b> or <b>desc</b>).
 * <h4>selected: array</h4>
 * Array for selected items.
 * <p>Example:</p>
 * <code><pre>{{'{'}}
 * 	showTableFooter: true,
 * 	showTableCount: false,
 * 	showTablePager: true,
 * 	rows: [
 * 		{{'{'}} name: 'Austin', city: 'London' {{'}'}},
 * 		{{'{'}} name: 'Molly', city: 'Tokyo' {{'}'}},
 * 		{{'{'}} name: 'Austin', city: 'New York' {{'}'}},
 * 		{{'{'}} name: 'Monica', city: 'Paris' {{'}'}},
 * 		{{'{'}} name: 'Austin', city: 'Dubai'{{'}'}}
 * 	],
 * 	rowHeight: 'auto',
 * 	columnMode: 'force',
 * 	limit: 3,
 * 	expandable: true,
 * 	rowExpand: 'single',
 * 	detailsHeight: 150,
 * 	expandIcon: this.myRowExpandIcon,
 * 	collapseIcon: this.myRowCollapseIcon,
 * 	selectable: true,
 * 	sorted: [{{'{'}}prop: 'name', dir: 'desc'{{'}'}}],
 * 	selected: this.mySelected
 * {{'}'}}</pre></code>
 */
properties: DatatableModel;
/**
 * <p> dynamicClass: function() -> string,	// Function that returns name of the class </p>
 * <p> class: string											  // Name of the css class selector </p>
 * <p> themeColor: string									// primary/accent/warn </p>
 * <code><pre>
 * {{'{'}}
 * 	// Container surrounding the all datatable elelemnts
 * 	container: {{'{'}}
 * 		dynamicClass,
 * 		class
 * 	{{'}'}},
 * 	// Header
 * 	header: {{'{'}}
 * 		dynamicClass,
 * 		class
 * 	{{'}'}},
 * 	// Ngx-datatable elelemnt
 * 	table: {{'{'}}
 * 		dynamicClass,
 * 		class
 * 	{{'}'}},
 * 	// Each cell container
 * 	tableCell: {{'{'}}
 * 		dynamicClass,
 * 		class
 * 	{{'}'}},
 * 	// Each row details section
 * 	tableRowDetails: {{'{'}}
 * 		dynamicClass,
 * 		class
 * 	{{'}'}}
 * 	// Row expanding icon styling
 * 	expandIcon: IconStylingModel, //refer to icon component
 * 	// Row collapsing icon styling
 * 	collapseIcon: IconStylingModel, //refer to icon component
 * 	// Footer
 * 	footer: {{'{'}}
 * 		dynamicClass,
 * 		class
 * 	{{'}'}},
 * {{'}'}}
 * </pre></code>
 * <p>Example:<p>
 * <code><pre>
 * 	new DatatableStylingModel({{'{'}}
 * 		container: {{'{'}}class: 'my-class-for-datatable-container'{{'}'}},
 * 		header: {{'{'}}class: 'my-class-for-datatable-header'{{'}'}},
 * 		table: {{'{'}}class: 'my-class-for-table'{{'}'}},
 * 		tableCell: {{'{'}}class: 'my-class-for-table-cell'{{'}'}},
 * 		tableRowDetails: {{'{'}}class: 'my-class-for-table-row-details'{{'}'}},
 * 		expandIcon: new IconStylingModel({{'{'}} icon: {{'{'}}themeColor: "primary"{{'}'}} {{'}'}}),
 * 		collapseIcon: new IconStylingModel({{'{'}} icon: {{'{'}}themeColor: "primary"{{'}'}} {{'}'}}),
 * 		footer: {{'{'}}class: 'my-class-for-datatable-footer'{{'}'}},
 * 	{{'}'}})
 * </pre></code>
 */
styling: DatatableStylingModel;
/**
 * If to show ngx-datatable footer
 * @param {?} value
 * @return {?}
 */
set showTableFooter(value: boolean) { this.properties.showTableFooter = value; }
/**
 * If to show ngx-datatable footer count text
 * @param {?} value
 * @return {?}
 */
set showTableCount(value: boolean) { this.properties.showTableCount = value; }
/**
 * If to show ngx-datatable footer pager
 * @param {?} value
 * @return {?}
 */
set showTablePager(value: boolean) { this.properties.showTablePager = value; }
/**
 * The height of the header in pixels. Type any. Pass a false for no header. Default: 42.
 * @param {?} value
 * @return {?}
 */
set headerHeight(value: any) { this.properties.headerHeight = value; }
/**
 * The height of the footer in pixels. Type any. Pass a false for no footer. Default: 42.
 * @param {?} value
 * @return {?}
 */
set footerHeight(value: any) { this.properties.footerHeight = value; }
/**
 * It is array with rows json objects.
 * @param {?} value
 * @return {?}
 */
set rows(value: any[]) { this.properties.rows = value; }
/**
 * It is height of each row. Can be numeric (nember will be transformed to pixels) or 'auto'. Default: 50.
 * @param {?} value
 * @return {?}
 */
set rowHeight(value: any) { this.properties.rowHeight = value; }
/**
 * The mode which the columns are distributed across the table. Type string. Can be: flex, force, standard. Default: force.
 * @param {?} value
 * @return {?}
 */
set columnMode(value: string) { this.properties.columnMode = value; }
/**
 * It is rows quontity per page. Type number. Default: 3. To show all rows: -1.
 * @param {?} value
 * @return {?}
 */
set limit(value: number) { this.properties.limit = value; }
/**
 * It is boolean value to use or not row details section. Default: false.
 * @param {?} value
 * @return {?}
 */
set expandable(value: boolean) { this.properties.expandable = value; }
/**
 * Means how rows can be expanded at one time. Default: multiple. Can be: multiple or single.
 * @param {?} value
 * @return {?}
 */
set rowExpand(value: string) { this.properties.rowExpand = value; }
/**
 * It is height of row in pixels. Type number. Default: 130.
 * @param {?} value
 * @return {?}
 */
set detailsHeight(value: number) { this.properties.detailsHeight = value; }
/**
 * CfIcon component for the row expanding icon.
 * @param {?} value
 * @return {?}
 */
set expandIcon(value: IconModel) { this.properties.expandIcon = value; }
/**
 * CfIcon component for the row collapsing icon.
 * @param {?} value
 * @return {?}
 */
set collapseIcon(value: IconModel) { this.properties.collapseIcon = value; }
/**
 * It is boolean value to show or not rows section checkboxes. Default: false.
 * @param {?} value
 * @return {?}
 */
set selectable(value: boolean) { this.properties.selectable = value; }
/**
 * Array with sorting objects.
 * Default: []. Rows can sorted by multipe columns.
 * Each sorting object is a json object with two properties:
 * <b>prop</b>(property name by which to sort rows) and <b>dir</b>(sort direction. Can be: <b>asc</b> or <b>desc</b>).
 * @param {?} value
 * @return {?}
 */
set sorted(value: any[]) { this.properties.sorted = value; }
/**
 * Array for selected items.
 * @param {?} value
 * @return {?}
 */
set selected(value: any[]) { this.properties.selected = value; }
/**
 * <p>Output event with two properties: type and value.</p>
 * <p>Type can be: all or row</p>
 * <p>When type is <b>all</b> - value can be <b>true</b>(all details rows was expanded) or
 * <b>false</b>(all details rows was compressed).</p>
 * <p>When type is <b>row</b> - value is an row json object.</p>
 */
onDetailsToggle: EventEmitter<any> = new EventEmitter<any>();
/**
 * <p>Output event with selected items array</p>
 */
onSelect: EventEmitter<any> = new EventEmitter<any>();
/**
 * <p>Output event with two properties: value and rows.</p>
 * <p>Value is a search string.</p>
 * <p>Rows is an array with filtered rows.</p>
 */
onFilter: EventEmitter<any> = new EventEmitter<any>();
/**
 * <p>Output event with four properties: column, newValue, prevValue, sorts.</p>
 * <p>Column is data with ngx-datatable column properties.</p>
 * <p>NewValue can be: <b>asc</b>(ascending sorting), <b>desc</b>(descending sorting) or <b>undefined</b>(not sorted).</p>
 * <p>PrevValue can be: <b>asc</b>(ascending sorting), <b>desc</b>(descending sorting) or <b>undefined</b>(not sorted).</p>
 * <p>Sorts is an array with sorting object(s).</p>
 */
onSort: EventEmitter<any> = new EventEmitter<any>();
/**
 * @hidden 
 * Object to register element state and compute differences.
 */
differ: any;
/**
 * @hidden
 */
_viewCols;
/**
 * @hidden
 * @param {?} _renderer
 * @param {?} elementRef
 * @param {?} templateService
 * @param {?} differs
 */
constructor(private _renderer: Renderer,
public elementRef: ElementRef,
    /**@hidden */ templateService: TemplateService,
    differs: IterableDiffers) {
    super(elementRef, templateService);
    this.differ = differs.find([]).create(null);
    this.properties = new DatatableModel();
    this.styling = new DatatableStylingModel();
  }
/**
 * @hidden
 * @return {?}
 */
ngOnInit() {
    this.getMyTemplate('datatable', DatatableTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'DATATABLE').then(sampler => {
        // console.log('DATATABLE', this);
      });
    });
  }
/**
 * @hidden
 * @param {?} col
 * @return {?}
 */
checkColProp(col) {
    if (col.properties) {
      let /** @type {?} */ propName = col.properties.sortBy !== '' ? col.properties.sortBy : col.properties.name.toLowerCase();
      return propName;
    }
  }
/**
 * @hidden
 * @return {?}
 */
expandAllRows() {
    this.originalRows.forEach(r => r['$$expanded'] = 1);
  }
/**
 * @hidden
 * @return {?}
 */
collapseAllRows() {
    this.originalRows.forEach(r => r['$$expanded'] = 0);
  }
/**
 * @hidden 
 * After content was initialized, will be created columns array definition
 * with names and templates and also will be created object for rows details templates.
 * @return {?}
 */
ngAfterContentInit() {
    if (this.cfDatatablePager) {
      this.cfDatatablePager.onPageSwitch.subscribe(page => {
        this.datatableElement.onFooterPage(page);
      });
    }
    if (this.cfDatatableRowsExpander) {
      this.cfDatatableRowsExpander.onToggle.subscribe(type => {
        if (this.properties.rowExpand !== 'single') {
          if (type === 'expand') {
            this.expandAllRows();
            this.datatableElement.rowDetail.expandAllRows();
          } else {
            this.collapseAllRows();
            this.datatableElement.rowDetail.collapseAllRows();
          }
        } else {
          if (type !== 'expand') {
            this.collapseAllRows();
            this.datatableElement.rowDetail.collapseAllRows();
          }
        }
      });
    }
    if (this.cfDatatableFilter) {
      this.cfDatatableFilter.onFilter.subscribe(value => {
        this.updateFilter(value);
      });
    }
  }
/**
 * @hidden
 * @param {?} align
 * @return {?}
 */
setColHeaderClasses(align) {
    let /** @type {?} */ a = align ? align : '';
    return 'table-' + this.id + '-col-header ' + a;
  }
/**
 * @hidden
 * @param {?} align
 * @return {?}
 */
setColCellClasses(align) {
    let /** @type {?} */ a = align ? align : '';
    return 'table-' + this.id + '-col-cells ' + a;
  }
/**
 * @hidden
 * @return {?}
 */
ngAfterViewInit() {
    this._viewCols = this.cfDatatableViewColumns.changes.subscribe(list => {
      if (this.datatableElement && this.properties != null && this.properties.columnMode === 'standard') {
        const /** @type {?} */ cols = this.tableColumns.toArray();
        let /** @type {?} */ start = 0;
        let /** @type {?} */ inRow = cols.length;
        let /** @type {?} */ rowCells = 'table-' + this.id + '-col-cells';
        let /** @type {?} */ headerCells = 'table-' + this.id + '-col-header';
        let /** @type {?} */ cells = this.datatableElement.element.getElementsByClassName(rowCells);
        let /** @type {?} */ headers = this.datatableElement.element.getElementsByClassName(headerCells);
        for (let /** @type {?} */ i = 0; i < inRow; ++i) {
          if (cols[i].properties.width !== '') {
            this._renderer.setElementStyle(headers[i], 'width', cols[i].properties.width);
          }
        }
        for (let /** @type {?} */ j = 0; j < cells.length; ++j) {
          this._renderer.setElementStyle(cells[j], 'width', cols[start].properties.width);
          start = start < inRow - 1 ? start + 1 : 0;
        }
      }
    })
  }
/**
 * @hidden
 * @return {?}
 */
ngDoCheck() {
    if (this.properties != null) {
      let /** @type {?} */ changes = this.differ.diff(this.properties.rows);
      if (changes) { this.updateRows(changes.collection); }
    }
  }
/**
 * @hidden
 * @return {?}
 */
ngOnDestroy() { this._viewCols.unsubscribe(); }
/**
 * @hidden
 * @param {?} event
 * @return {?}
 */
onPageChange(event) { if (this.cfDatatablePager) { this.cfDatatablePager._selectPage(event.offset + 1); } }
/**
 * @hidden
 * @param {?} newRows
 * @return {?}
 */
updateRows(newRows) {
    const /** @type {?} */ rowsLength = newRows.length;
    this.originalRows = [...newRows];
    this.clonedRows = [...this.originalRows];
    if (this.cfDatatablePager) {
      this.cfDatatablePager._updatePager(rowsLength, this.properties.limit);
    }
  }
/**
 * @hidden 
 * Function for filtering Datatable. Goes to first page of Datatable after filtering.
 * @param {?} event
 * @return {?}
 */
updateFilter(event) {
    const /** @type {?} */ val = event;
    const /** @type {?} */ filtered = this.properties.rows.filter(function (d) {
      return JSON.stringify(d).toLowerCase().indexOf(val) !== -1 || !val;
    });
    this.clonedRows = filtered;
    this.datatableElement.offset = 0;
    this.onFilter.emit({ value: val, rows: this.clonedRows });
    if (this.cfDatatablePager) {
      this.cfDatatablePager._updatePager(filtered.length, this.properties.limit, 1);
    }
  }
/**
 * @hidden 
 * Function for selection rows with rows checkboxes.
 * @param {?} __0
 * @return {?}
 */
onSelectRows({ selected }) {
    this.properties.selected.splice(0, this.properties.selected.length);
    this.properties.selected.push(...selected);
    this.onSelect.emit(selected);
  }
/**
 * @hidden 
 * Function for toggle each row details section.
 * @param {?} row
 * @return {?}
 */
toggleExpandRow(row) {
    if (this.properties.rowExpand === 'single') {
      const /** @type {?} */ currIndex = this.originalRows.indexOf(row), /** @type {?} */ currExpanded = row['$$expanded'];
      this.datatableElement.rowDetail.toggleExpandRow(row);
      this.originalRows.forEach((r, i) => {
        if (i !== currIndex && r['$$expanded']) {
          this.datatableElement.rowDetail.toggleExpandRow(r);
        }
      });
    } else {
      this.datatableElement.rowDetail.toggleExpandRow(row);
    }
  }
/**
 * @hidden 
 * Function to emit toggle event
 * @param {?} event 		object with type and value properties
 * @return {?}
 */
onDetailToggle(event) { this.onDetailsToggle.emit(event); }
/**
 * @hidden 
 * Function to emit sorting event
 * @param {?} event 		sort object
 * @return {?}
 */
onSortRows(event) { this.onSort.emit(event); }
/**
 * @hidden 
 * Function for updating Datatable when rows length was changed by user custom functions.
 * @param {?} type
 * @param {?} i
 * @param {?} it
 * @return {?}
 */
checkFiltered(type: string, i: number, it: Object) {
    if (this.properties.rows.length !== this.originalRows.length) {
      this.originalRows.splice(0, this.originalRows.length);
      this.originalRows = [...this.properties.rows];
      this.clonedRows = [...this.originalRows];
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-datatable',
  template: "<div *ngIf=\"_inited && properties?.display\" class=\"{{activeTemplate.style?.container?.class}} {{styling?.container?.class}} {{getDynamicClasses(activeTemplate.style?.container?.dynamicClass)}} {{getDynamicClasses(styling?.container?.dynamicClass)}}\" [ngClass]=\"'cf-datatable'\"> <div class=\"{{activeTemplate.style?.header?.class}} {{styling?.header?.class}} {{getDynamicClasses(activeTemplate.style?.header?.dynamicClass)}} {{getDynamicClasses(styling?.header?.dynamicClass)}}\" [ngClass]=\"'cf-datatable-header'\"> <ng-content select=\"[header]\"></ng-content> </div> <ngx-datatable #datatableElement class=\"material {{activeTemplate.style?.table?.class}} {{styling?.table?.class}} {{getDynamicClasses(activeTemplate.style?.table?.dynamicClass)}} {{getDynamicClasses(styling?.table?.dynamicClass)}}\" [ngClass]=\"'cf-datatable-table'\" [headerHeight]=\"properties?.headerHeight\" [footerHeight]=\"properties?.footerHeight\" [columns]=\"properties?.columns\" [columnMode]=\"properties?.columnMode\" [rowHeight]=\"properties?.rowHeight\" [rows]=\"clonedRows\" [selected]=\"properties?.selected\" [selectionType]=\"properties?.selectable ? 'checkbox' : false\" [sortType]=\"multi\" [sorts]=\"properties?.sorted\" [limit]=\"properties.limit && properties.limit < 0 ? undefined : properties.limit\" (select)=\"onSelectRows($event)\" (sort)=\"onSortRows($event)\" (page)=\"onPageChange($event)\"> <ngx-datatable-row-detail *ngIf=\"properties?.expandable\" [rowHeight]=\"properties?.detailsHeight\" (toggle)=\"onDetailToggle($event)\"> <ng-template let-row=\"row\" let-index=\"row?.$$index\" ngx-datatable-row-detail-template> <div class=\"{{activeTemplate.style?.tableRowDetails?.class}} {{styling?.tableRowDetails?.class}} {{getDynamicClasses(activeTemplate.style?.tableRowDetails?.dynamicClass)}} {{getDynamicClasses(styling?.tableRowDetails?.dynamicClass)}}\" [ngClass]=\"'cf-datatable-row-details'\"> <div [ngTemplateOutlet]=\"details\" [ngOutletContext]=\"{cfIndex: row?.$$index, cfRow: row}\"></div> </div> </ng-template> </ngx-datatable-row-detail> <ngx-datatable-column *ngIf=\"properties?.expandable\" [width]=\"30\" [resizeable]=\"false\" [sortable]=\"false\" [draggable]=\"false\" [canAutoResize]=\"false\"> <ng-template let-row=\"row\" ngx-datatable-cell-template> <cf-icon *ngIf=\"!row?.$$expanded\" [properties]=\"properties?.expandIcon\" [styling]=\"styling?.expandIcon\" (click)=\"toggleExpandRow(row)\"></cf-icon> <cf-icon *ngIf=\"row?.$$expanded\" [properties]=\"properties?.collapseIcon\" [styling]=\"styling?.collapseIcon\" (click)=\"toggleExpandRow(row)\"></cf-icon> </ng-template> </ngx-datatable-column> <ngx-datatable-column *ngIf=\"properties?.selectable\" [width]=\"30\" [sortable]=\"false\" [canAutoResize]=\"false\" [draggable]=\"false\" [resizeable]=\"false\" [headerCheckboxable]=\"true\" [checkboxable]=\"true\"></ngx-datatable-column> <ngx-datatable-column #cfDatatableViewColumn *ngFor=\"let col of tableColumns\" [name]=\"col?.properties.name\" [sortable]=\"true\" [prop]=\"checkColProp(col)\" [draggable]=\"false\" [headerClass]=\"setColHeaderClasses(col.properties.headerAlignment)\" [cellClass]=\"setColCellClasses(col.properties.cellAlignment)\"> <ng-template *ngIf=\"col?.header\" ngx-datatable-header-template let-row=\"row\" let-column=\"col\" let-value=\"value\" let-index=\"row?.$$index\" let-sort=\"sortFn\"> <span class=\"datatable-header-cell-wrapper\" (click)=\"sort()\"> <span class=\"{{col?.activeTemplate?.style?.header?.class}} {{col.styling?.header?.class}} {{getDynamicClasses(col?.activeTemplate?.style?.header?.dynamicClass)}} {{getDynamicClasses(col.styling?.header?.dynamicClass)}}\" [ngClass]=\"'datatable-header-cell-label'\"> <div [ngTemplateOutlet]=\"col?.header\"></div> </span> </span> </ng-template> <ng-template *ngIf=\"col?.content\" let-row=\"row\" let-column=\"col\" let-value=\"value\" let-index=\"row?.$$index\" ngx-datatable-cell-template> <div class=\"{{activeTemplate.style?.tableCell?.class}} {{styling?.tableCell?.class}} {{col?.activeTemplate?.style?.container?.class}} {{col?.styling?.container?.class}} {{getDynamicClasses(activeTemplate?.style?.tableCell?.dynamicClass)}} {{getDynamicClasses(styling?.tableCell?.dynamicClass)}} {{getDynamicClasses(col?.activeTemplate?.style?.container?.dynamicClass)}} {{getDynamicClasses(col?.styling?.container?.dynamicClass)}}\" [ngClass]=\"'cf-datatable-cell'\"> <div [ngTemplateOutlet]=\"col?.content\" [ngOutletContext]=\"{cfIndex: row?.$$index, cfRow: row}\"></div> </div> </ng-template> </ngx-datatable-column> <ngx-datatable-footer> <ng-template ngx-datatable-footer-template let-rowCount=\"rowCount\" let-pageSize=\"pageSize\" let-selectedCount=\"selectedCount\" let-curPage=\"curPage\" let-offset=\"offset\"> <div *ngIf=\"properties?.showTableFooter\" class=\"default-datatable-footer\"> <div *ngIf=\"properties?.showTableCount\" class=\"page-count\"> <span *ngIf=\"selectedCount\"> {{selectedCount.toLocaleString()}} {{datatableElement.messages.selectedMessage}} / </span> {{rowCount.toLocaleString()}} {{datatableElement.messages.totalMessage}} </div> <datatable-pager *ngIf=\"properties?.showTablePager\" [pagerLeftArrowIcon]=\"'datatable-icon-left'\" [pagerRightArrowIcon]=\"'datatable-icon-right'\" [pagerPreviousIcon]=\"'datatable-icon-prev'\" [pagerNextIcon]=\"'datatable-icon-skip'\" [page]=\"curPage\" [size]=\"pageSize\" [count]=\"rowCount\" [hidden]=\"!(rowCount / pageSize > 1)\" (change)=\"datatableElement.onFooterPage($event)\"> </datatable-pager> </div> </ng-template> </ngx-datatable-footer> </ngx-datatable> <div class=\"{{activeTemplate.style?.footer?.class}} {{styling?.footer?.class}} {{getDynamicClasses(activeTemplate.style?.footer?.dynamicClass)}} {{getDynamicClasses(styling?.footer?.dynamicClass)}}\" [ngClass]=\"'cf-datatable-footer'\"> <ng-content select=\"[footer]\"></ng-content> </div> </div> ",
  styles: [":host ::ng-deep { display: block; box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.12), 0 1px 2px 0 rgba(0, 0, 0, 0.24); } :host ::ng-deep .cf-datatable-header { border-bottom: 1px solid #e0e0e0; } :host ::ng-deep .cf-datatable-footer { border-top: 1px solid #e0e0e0; } :host ::ng-deep datatable-body.datatable-body { overflow: auto; } :host ::ng-deep .datatable-header-cell > div { display: flex; align-items: center; } :host ::ng-deep .datatable-header-cell.start > div { justify-content: flex-start; } :host ::ng-deep .datatable-header-cell.center > div { justify-content: center; } :host ::ng-deep .datatable-header-cell.end > div { justify-content: flex-end; } :host ::ng-deep .cf-datatable-cell { display: flex; width: 100%; } :host ::ng-deep .datatable-header-inner { width: 100% !important; } :host ::ng-deep .datatable-row-center { width: 100% !important; } :host ::ng-deep datatable-body-row.datatable-body-row { width: 100% !important; } :host ::ng-deep .datatable-row-center.datatable-row-group { width: 100% !important; } :host ::ng-deep .datatable-body-cell { display: flex !important; align-items: center; } :host ::ng-deep .datatable-body-cell.start .cf-datatable-cell { justify-content: flex-start; } :host ::ng-deep .datatable-body-cell.center .cf-datatable-cell { justify-content: center; } :host ::ng-deep .datatable-body-cell.end .cf-datatable-cell { justify-content: flex-end; } :host ::ng-deep .datatable-checkbox { padding: 0px !important; } :host ::ng-deep .ngx-datatable .datatable-body .datatable-scroll { width: 100% !important; } :host ::ng-deep .rotated { transform: rotate(-45deg); } :host ::ng-deep .datatable-body-cell-label { display: flex; white-space: initial; width: 100%; max-width: 100%; max-height: 100%; } :host ::ng-deep .ngx-datatable { box-shadow: none !important; } :host ::ng-deep .ngx-datatable .datatable-footer { border-top: none !important; } :host ::ng-deep .ngx-datatable .datatable-footer .datatable-footer-inner { height: auto !important; } :host ::ng-deep .default-datatable-footer { display: flex; align-items: center; width: 100%; min-height: 42px; border-top: 1px solid rgba(0, 0, 0, 0.12); } .cf-datatable-top-options { display: flex; flex-direction: row; justify-content: space-between; padding: 8px 16px; border-bottom: 1px solid rgba(0, 0, 0, 0.08); box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.12), 0 1px 2px 0 rgba(0, 0, 0, 0.24); } .cf-datatable-top-options .cf-datatable-allrow-togglers { display: flex; flex-grow: 1; align-items: center; } .cf-datatable-top-options cf-icon { margin-right: 2px; transition: all 0.2s ease; } .cf-datatable-top-options cf-icon:hover { transform: scale(1.1); } .cf-datatable-top-options cf-input { display: flex; flex-grow: 1; justify-content: flex-end; } .cf-datatable-top-options cf-input ::ng-deep .mat-input-wrapper { padding-bottom: 0; } .cf-datatable-top-options cf-input ::ng-deep .mat-input-underline { bottom: 1px; } .cf-datatable-toggle-row { color: #757575; display: flex; width: 16px; height: 16px; text-decoration: none !important; outline: none !important; } "]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer, },
{type: ElementRef, },
{type: TemplateService, },
{type: IterableDiffers, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'tableColumns': [{ type: ContentChildren, args: [CfDatatableColumnComponent, ] },],
'details': [{ type: ContentChild, args: ['details', ] },],
'cfDatatablePager': [{ type: ContentChild, args: [CfDatatablePagerComponent, ] },],
'cfDatatableFilter': [{ type: ContentChild, args: [CfDatatableFilterComponent, ] },],
'cfDatatableRowsExpander': [{ type: ContentChild, args: [CfDatatableRowsExpanderComponent, ] },],
'datatableElement': [{ type: ViewChild, args: ['datatableElement', ] },],
'cfDatatableViewColumns': [{ type: ViewChildren, args: ['cfDatatableViewColumn', ] },],
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'showTableFooter': [{ type: Input, args: ['showTableFooter', ] },],
'showTableCount': [{ type: Input, args: ['showTableCount', ] },],
'showTablePager': [{ type: Input, args: ['showTablePager', ] },],
'headerHeight': [{ type: Input, args: ['headerHeight', ] },],
'footerHeight': [{ type: Input, args: ['footerHeight', ] },],
'rows': [{ type: Input, args: ['rows', ] },],
'rowHeight': [{ type: Input, args: ['rowHeight', ] },],
'columnMode': [{ type: Input, args: ['columnMode', ] },],
'limit': [{ type: Input, args: ['limit', ] },],
'expandable': [{ type: Input, args: ['expandable', ] },],
'rowExpand': [{ type: Input, args: ['rowExpand', ] },],
'detailsHeight': [{ type: Input, args: ['detailsHeight', ] },],
'expandIcon': [{ type: Input, args: ['expandIcon', ] },],
'collapseIcon': [{ type: Input, args: ['collapseIcon', ] },],
'selectable': [{ type: Input, args: ['selectable', ] },],
'sorted': [{ type: Input, args: ['sorted', ] },],
'selected': [{ type: Input, args: ['selected', ] },],
'onDetailsToggle': [{ type: Output },],
'onSelect': [{ type: Output },],
'onFilter': [{ type: Output },],
'onSort': [{ type: Output },],
};
}

function CfDatatableComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfDatatableComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfDatatableComponent.ctorParameters;
/** @type {?} */
CfDatatableComponent.propDecorators;
/**
 * @hidden 
 * List of datatable column components with column templates and names.
 * @type {?}
 */
CfDatatableComponent.prototype.tableColumns;
/**
 * @hidden 
 * Datatable row details template
 * @type {?}
 */
CfDatatableComponent.prototype.details;
/**
 * @hidden
 * @type {?}
 */
CfDatatableComponent.prototype.cfDatatablePager;
/**
 * @hidden
 * @type {?}
 */
CfDatatableComponent.prototype.cfDatatableFilter;
/**
 * @hidden
 * @type {?}
 */
CfDatatableComponent.prototype.cfDatatableRowsExpander;
/**
 * @hidden 
 * Component property for registering Datatable and for connection to <b>ngx-datatable</b> functionality
 * @type {?}
 */
CfDatatableComponent.prototype.datatableElement;
/**
 * @hidden
 * @type {?}
 */
CfDatatableComponent.prototype.cfDatatableViewColumns;
/**
 * @hidden 
 * Datatable original rows.
 * Needs to be to keep original rows available after iteracting with datatable functionality
 * (for exapmle after filtering).
 * @type {?}
 */
CfDatatableComponent.prototype.originalRows;
/**
 * @hidden 
 * Working rows list which is changing by user iteraction with datatable functionality.
 * @type {?}
 */
CfDatatableComponent.prototype.clonedRows;
/**
 * <code><pre>{{'{'}}
 * 	showTableFooter: boolean,
 * 	showTableCount: boolean,
 * 	showTablePager: boolean,
 * 	headerHeight: any,
 * 	footerHeight: any,
 * 	rows: any[],
 * 	rowHeight: any,
 * 	columnMode: string,
 * 	limit: number,
 * 	expandable: boolean,
 * 	rowExpand: string,
 * 	detailsHeight: number,
 * 	expandIcon: IconModel,
 * 	collapseIcon: IconModel,
 * 	selectable: boolean,
 * 	sorted: any[],
 * 	selected: any[],
 * {{'}'}}</pre></code>
 * <h4>showTableFooter: boolean</h4>
 * If to show ngx-datatable footer
 * <h4>showTableCount: boolean</h4>
 * If to show ngx-datatable footer count text
 * <h4>showTablePager: boolean</h4>
 * If to show ngx-datatable footer pager
 * <h4>headerHeight: any</h4>
 * The height of the header in pixels. Type any. Pass a false for no header. Default: 42.
 * <h4>footerHeight: any</h4>
 * The height of the footer in pixels. Type any. Pass a false for no footer. Default: 42.
 * <h4>rows: any[]</h4>
 * It is array with rows json objects.
 * <h4>rowHeight: any</h4>
 * It is height of each row. Can be numeric (nember will be transformed to pixels) or 'auto'. Default: 50.
 * <h4>columnMode: string</h4>
 * The mode which the columns are distributed across the table. Type string. Can be: flex, force, standard. Default: force.
 * <h4>limit: number</h4>
 * It is rows quontity per page. Type number. Default: 3. To show all rows: -1.
 * <h4>expandable: boolean</h4>
 * It is boolean value to use or not row details section. Default: false.
 * <h4>rowExpand: string</h4>
 * Means how rows can be expanded at one time. Can be: multiple or single. Default: multiple.
 * <h4>detailsHeight: number</h4>
 * It is height of row in pixels. Type number. Default: 130.
 * <h4>expandIcon: IconModel</h4>
 * CfIcon component for the row expanding icon.
 * <h4>collapseIcon: IconModel</h4>
 * CfIcon component for the row collapsing icon.
 * <h4>selectable: boolean</h4>
 * It is boolean value to show or not rows section checkboxes. Default: false.
 * <h4>sorted: any[]</h4>
 * Array with sorting objects. Default: [].
 * Rows can sorted by multipe columns. Each sorting object is a json object with two properties:
 * <b>prop</b>(property name by which to sort rows) and <b>dir</b>(sort direction. Can be: <b>asc</b> or <b>desc</b>).
 * <h4>selected: any[]</h4>
 * Array for selected items.
 * <h4>sorted: array</h4>
 * Array with sorting objects. Default: [].
 * Rows can sorted by multipe columns.
 * Each sorting object is a json object with two properties:
 * <b>prop</b>(property name by which to sort rows) and <b>dir</b>(sort direction. Can be: <b>asc</b> or <b>desc</b>).
 * <h4>selected: array</h4>
 * Array for selected items.
 * <p>Example:</p>
 * <code><pre>{{'{'}}
 * 	showTableFooter: true,
 * 	showTableCount: false,
 * 	showTablePager: true,
 * 	rows: [
 * 		{{'{'}} name: 'Austin', city: 'London' {{'}'}},
 * 		{{'{'}} name: 'Molly', city: 'Tokyo' {{'}'}},
 * 		{{'{'}} name: 'Austin', city: 'New York' {{'}'}},
 * 		{{'{'}} name: 'Monica', city: 'Paris' {{'}'}},
 * 		{{'{'}} name: 'Austin', city: 'Dubai'{{'}'}}
 * 	],
 * 	rowHeight: 'auto',
 * 	columnMode: 'force',
 * 	limit: 3,
 * 	expandable: true,
 * 	rowExpand: 'single',
 * 	detailsHeight: 150,
 * 	expandIcon: this.myRowExpandIcon,
 * 	collapseIcon: this.myRowCollapseIcon,
 * 	selectable: true,
 * 	sorted: [{{'{'}}prop: 'name', dir: 'desc'{{'}'}}],
 * 	selected: this.mySelected
 * {{'}'}}</pre></code>
 * @type {?}
 */
CfDatatableComponent.prototype.properties;
/**
 * <p> dynamicClass: function() -> string,	// Function that returns name of the class </p>
 * <p> class: string											  // Name of the css class selector </p>
 * <p> themeColor: string									// primary/accent/warn </p>
 * <code><pre>
 * {{'{'}}
 * 	// Container surrounding the all datatable elelemnts
 * 	container: {{'{'}}
 * 		dynamicClass,
 * 		class
 * 	{{'}'}},
 * 	// Header
 * 	header: {{'{'}}
 * 		dynamicClass,
 * 		class
 * 	{{'}'}},
 * 	// Ngx-datatable elelemnt
 * 	table: {{'{'}}
 * 		dynamicClass,
 * 		class
 * 	{{'}'}},
 * 	// Each cell container
 * 	tableCell: {{'{'}}
 * 		dynamicClass,
 * 		class
 * 	{{'}'}},
 * 	// Each row details section
 * 	tableRowDetails: {{'{'}}
 * 		dynamicClass,
 * 		class
 * 	{{'}'}}
 * 	// Row expanding icon styling
 * 	expandIcon: IconStylingModel, //refer to icon component
 * 	// Row collapsing icon styling
 * 	collapseIcon: IconStylingModel, //refer to icon component
 * 	// Footer
 * 	footer: {{'{'}}
 * 		dynamicClass,
 * 		class
 * 	{{'}'}},
 * {{'}'}}
 * </pre></code>
 * <p>Example:<p>
 * <code><pre>
 * 	new DatatableStylingModel({{'{'}}
 * 		container: {{'{'}}class: 'my-class-for-datatable-container'{{'}'}},
 * 		header: {{'{'}}class: 'my-class-for-datatable-header'{{'}'}},
 * 		table: {{'{'}}class: 'my-class-for-table'{{'}'}},
 * 		tableCell: {{'{'}}class: 'my-class-for-table-cell'{{'}'}},
 * 		tableRowDetails: {{'{'}}class: 'my-class-for-table-row-details'{{'}'}},
 * 		expandIcon: new IconStylingModel({{'{'}} icon: {{'{'}}themeColor: "primary"{{'}'}} {{'}'}}),
 * 		collapseIcon: new IconStylingModel({{'{'}} icon: {{'{'}}themeColor: "primary"{{'}'}} {{'}'}}),
 * 		footer: {{'{'}}class: 'my-class-for-datatable-footer'{{'}'}},
 * 	{{'}'}})
 * </pre></code>
 * @type {?}
 */
CfDatatableComponent.prototype.styling;
/**
 * <p>Output event with two properties: type and value.</p>
 * <p>Type can be: all or row</p>
 * <p>When type is <b>all</b> - value can be <b>true</b>(all details rows was expanded) or
 * <b>false</b>(all details rows was compressed).</p>
 * <p>When type is <b>row</b> - value is an row json object.</p>
 * @type {?}
 */
CfDatatableComponent.prototype.onDetailsToggle;
/**
 * <p>Output event with selected items array</p>
 * @type {?}
 */
CfDatatableComponent.prototype.onSelect;
/**
 * <p>Output event with two properties: value and rows.</p>
 * <p>Value is a search string.</p>
 * <p>Rows is an array with filtered rows.</p>
 * @type {?}
 */
CfDatatableComponent.prototype.onFilter;
/**
 * <p>Output event with four properties: column, newValue, prevValue, sorts.</p>
 * <p>Column is data with ngx-datatable column properties.</p>
 * <p>NewValue can be: <b>asc</b>(ascending sorting), <b>desc</b>(descending sorting) or <b>undefined</b>(not sorted).</p>
 * <p>PrevValue can be: <b>asc</b>(ascending sorting), <b>desc</b>(descending sorting) or <b>undefined</b>(not sorted).</p>
 * <p>Sorts is an array with sorting object(s).</p>
 * @type {?}
 */
CfDatatableComponent.prototype.onSort;
/**
 * @hidden 
 * Object to register element state and compute differences.
 * @type {?}
 */
CfDatatableComponent.prototype.differ;
/**
 * @hidden
 * @type {?}
 */
CfDatatableComponent.prototype._viewCols;
/** @type {?} */
CfDatatableComponent.prototype._renderer;
/** @type {?} */
CfDatatableComponent.prototype.elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
